import api from "@/app/api";
import type {
  AdvancedProductSearchResult,
  OrderHeader,
  OrderLine,
} from "@/app/services/generated/orderService";
import { OrderService } from "@/app/services/generated/orderService";
import type {
  Outlet,
  OutletSummary,
} from "@/app/services/generated/outletService";
import { outletService } from "@/app/services/generated/outletService";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import type Guid from "@/app/types/common/guid";
import { GuidSet, type GuidMap } from "@/app/types/common/guidcollections";
import { translateWithParams, useI18n } from "@/plugins/i18n";
import type { PackageType } from "@/app/services/generated/packagetypeService";
import type { Group } from "@/app/services/generated/groupService";
import { itemAtLocationService } from "../generated/itematlocationService";

const { $m, $t } = useI18n();
export default class OrderServiceExtended extends OrderService {
  /**
   * Generate a file name for an invoice given an order ID
   */
  public async generateInvoiceFilename(orderId: Guid): Promise<string> {
    let filename = "";
    let orderHeaderWithInvoiceNumber: OrderHeader | null = null;

    const order = (
      await orderService.getOrders(api.tenantId, [orderId], false)
    )[0];

    if (
      order.header.readOnly.invoiceNumber === "" &&
      !order.header.readOnly.isRetailOrder
    ) {
      orderHeaderWithInvoiceNumber = (
        await orderService.assignInvoiceNumbers(api.tenantId, [
          order.header.readOnly.id,
        ])
      )[0];
    }

    // if retail order, set filename to outlet code + date
    if (order.header.readOnly.isRetailOrder) {
      const outlet = await outletService.getOutlet(
        api.tenantId,
        order.header.readOnly.outletId
      );
      filename = translateWithParams("orders_file_template_pdf", {
        number: outlet.readOnly.code + tenantService.today.toString(),
      }).toString();
    } else {
      filename = translateWithParams("orders_file_template_pdf", {
        number: orderHeaderWithInvoiceNumber
          ? orderHeaderWithInvoiceNumber.readOnly.invoiceNumber
          : order.header.readOnly.invoiceNumber,
      }).toString();
    }

    return filename;
  }

  sortOrderLinesByPackageType(
    packageTypeMap: GuidMap<Readonly<PackageType> | null>
  ) {
    return (lineA: OrderLine, lineB: OrderLine) => {
      // Sort by package Type name
      const packageTypeA = packageTypeMap.get(lineA.readOnly.stockItemId);
      const packageTypeB = packageTypeMap.get(lineB.readOnly.stockItemId);

      const packageCompare = (packageTypeA?.id.toString() ?? "").localeCompare(
        packageTypeB?.id.toString() ?? ""
      );

      if (packageCompare === 0) {
        // Within package Type Sort Name Alphabetically
        return lineA.productName.localeCompare(lineB.productName);
      }
      return packageCompare;
    };
  }

  shouldEmailInvoices(
    outlet: Readonly<Outlet | OutletSummary> | null,
    group: Readonly<Group> | null
  ) {
    return group && !group.sendInvoiceToOutlet
      ? group.emailInvoices
      : !!outlet?.emailInvoices;
  }

  async filterProductSearchResultsByAllowSales(
    toFilter: readonly AdvancedProductSearchResult[],
    stockLocationId: Guid,
    includeHidden: boolean
  ) {
    const disallowedItemIds = (
      await itemAtLocationService.getItemAtLocations(
        api.tenantId,
        toFilter.map((result) => result.stockItemId),
        [stockLocationId]
      )
    )
      .filter((x) => !x.allowSales)
      .map((x) => x.stockItemId);

    const disallowedItemsHash = new GuidSet(disallowedItemIds);

    return toFilter.filter(
      (result) =>
        (includeHidden || !result.isHidden) &&
        !disallowedItemsHash.has(result.stockItemId)
    );
  }

  // Mimic what advanced product search does, but splice in "allowSales" behaviours at locations
  // we could make take the same arguments as advancedProductSearch Paramters<OrderService["advancedProductSearch"]>
  // but we want this to 100% break if the api changes
  async advancedProductSearchWithRestrictedSales(
    tenantId: Guid,
    orderId: Guid,
    stockLocationId: Guid,
    productBrandId: Guid | null,
    packageTypeId: Guid | null,
    supplierId: Guid | null,
    includeHidden: boolean
  ): Promise<AdvancedProductSearchResult[]> {
    const searchResults = await this.advancedProductSearch(
      tenantId,
      orderId,
      stockLocationId,
      productBrandId,
      packageTypeId,
      supplierId
    );

    return await this.filterProductSearchResultsByAllowSales(
      searchResults,
      stockLocationId,
      includeHidden
    );
  }

  async advancedProductSearchByOrderHeader(
    tenantId: Guid,
    header: OrderHeader,
    stockLocationId: Guid,
    productBrandId: Guid | null,
    packageTypeId: Guid | null,
    supplierId: Guid | null
  ): Promise<AdvancedProductSearchResult[]> {
    return await this.advancedProductSearch2(
      tenantId,
      header.despatchDate,
      header.priceListId,
      stockLocationId,
      header.vatCodeId,
      header.readOnly.outletId,
      header.readOnly.groupId,
      header.standardOrderType,
      productBrandId,
      packageTypeId,
      supplierId
    );
  }
}

export const orderService = new OrderServiceExtended();
